import { useMemo } from 'react';
import _ from 'lodash';
import { produce } from 'immer';
import { metricsStore } from '@globalStore/metricsStore';
import { createMetricsGroupedByCategoryForAProduct } from '@utils/metrics/utilities';
import { FLOW_METRICS_DISPLAY, FRUSTRATION_SIGNAL_METRICS_DISPLAY } from '@src/utils/flow/meta';

export function generateFlowMetrics(allMetrics, currentFlowMeta) {
  const flowMapping = currentFlowMeta?.metrics;
  const flowMetaMetrics = currentFlowMeta?.metaDefinition?.metrics || [];
  if (!flowMapping || !flowMetaMetrics) return allMetrics;
  // Invert the mapping to get identifier by metric key
  const invertMapping = _.invert(flowMapping || {});
  return flowMetaMetrics.reduce((acc, { identifier, category, active }) => {
    const metric = allMetrics[identifier];

    if (!metric) return acc;

    acc[identifier] = produce(metric, (draft) => {
      draft.displayName =
        FLOW_METRICS_DISPLAY[invertMapping[identifier]]?.name || metric.displayName;
      draft.short = FLOW_METRICS_DISPLAY[invertMapping[identifier]]?.name || metric.short;
      draft.category = category;

      // Override category and active status if it's provided in flowMetaMetrics
      if (typeof active === 'boolean') {
        draft.active = active;
      }
    });

    const { percentageMetric, defaultSecondaryMetric } = metric;

    if (percentageMetric && allMetrics[percentageMetric] && !acc[percentageMetric]) {
      acc[percentageMetric] = { ...allMetrics[percentageMetric], category };
    }

    if (
      defaultSecondaryMetric &&
      allMetrics[defaultSecondaryMetric] &&
      !acc[defaultSecondaryMetric]
    ) {
      acc[defaultSecondaryMetric] = { ...allMetrics[defaultSecondaryMetric], category };
    }

    return acc;
  }, {});
}

export function generateFrustrationSignalMetrics(
  allMetrics: any,
  currentFrustrationSignalMeta: any,
): any {
  const frustrationSignalMapping = currentFrustrationSignalMeta?.metrics;
  if (!frustrationSignalMapping) {
    return {};
  }

  // Get frustrationSignalMetricsMeta from store for simplified names
  const { frustrationSignalMetricsMeta } = metricsStore.getState();
  const signalId = currentFrustrationSignalMeta?.id;
  const signalMetricsMeta = frustrationSignalMetricsMeta?.[signalId];

  // Invert the mapping to get identifier by metric key
  const invertMapping = _.invert(frustrationSignalMapping || {});
  const metricKeys = Object.keys(invertMapping); // Use inverted mapping keys, not original mapping keys

  return metricKeys.reduce((acc, metricKey) => {
    const metric = allMetrics[metricKey];
    if (!metric) return acc;

    acc[metricKey] = produce(metric, (draft) => {
      // Apply simplified names from frustrationSignalMetricsMeta first, then fallback to other sources
      const frustrationSignalDisplayName = signalMetricsMeta?.[metricKey]?.displayName;
      const frustrationSignalShort = signalMetricsMeta?.[metricKey]?.short;

      // Fallback to local signal config metricsDisplay, then global FRUSTRATION_SIGNAL_METRICS_DISPLAY
      const localDisplayName =
        currentFrustrationSignalMeta.metricsDisplay?.[invertMapping[metricKey]]?.name;
      const globalDisplayName = FRUSTRATION_SIGNAL_METRICS_DISPLAY[invertMapping[metricKey]]?.name;
      const localShortName =
        currentFrustrationSignalMeta.metricsDisplay?.[invertMapping[metricKey]]?.shortName;
      const globalShortName =
        FRUSTRATION_SIGNAL_METRICS_DISPLAY[invertMapping[metricKey]]?.shortName;

      // Priority: frustrationSignalMetricsMeta > local config > global config > original
      draft.displayName =
        frustrationSignalDisplayName || localDisplayName || globalDisplayName || metric.displayName;
      draft.short = frustrationSignalShort || localShortName || globalShortName || metric.short;
      // Don't override category - keep original category for proper filtering
    });

    // Handle percentage metrics
    const { percentageMetric, defaultSecondaryMetric } = metric;
    if (percentageMetric && allMetrics[percentageMetric] && !acc[percentageMetric]) {
      acc[percentageMetric] = produce(allMetrics[percentageMetric], (draft) => {
        // Apply the same display names to percentage metrics
        const frustrationSignalDisplayName = signalMetricsMeta?.[metricKey]?.displayName;
        const frustrationSignalShort = signalMetricsMeta?.[metricKey]?.short;

        const localDisplayName =
          currentFrustrationSignalMeta.metricsDisplay?.[invertMapping[metricKey]]?.name;
        const globalDisplayName =
          FRUSTRATION_SIGNAL_METRICS_DISPLAY[invertMapping[metricKey]]?.name;
        const localShortName =
          currentFrustrationSignalMeta.metricsDisplay?.[invertMapping[metricKey]]?.shortName;
        const globalShortName =
          FRUSTRATION_SIGNAL_METRICS_DISPLAY[invertMapping[metricKey]]?.shortName;

        draft.displayName =
          frustrationSignalDisplayName ||
          localDisplayName ||
          globalDisplayName ||
          metric.displayName;
        draft.short = frustrationSignalShort || localShortName || globalShortName || metric.short;
        // Don't override category - keep original category for proper filtering
      });
    }

    if (
      defaultSecondaryMetric &&
      allMetrics[defaultSecondaryMetric] &&
      !acc[defaultSecondaryMetric]
    ) {
      acc[defaultSecondaryMetric] = { ...allMetrics[defaultSecondaryMetric] };
    }

    return acc;
  }, {});
}

// Utility function to apply limitation filters to grouped metrics
function applyLimitationFilters(
  metricsGroupedByCategory: any,
  metricsCategoryKeys: any,
  excludeCategories: string[] = [],
  excludeMetrics: string[] = [],
  limitation: any = null,
) {
  const currentMetricCategory = Object.assign({}, metricsGroupedByCategory);

  Object.keys(currentMetricCategory).forEach((categoryKey: any) => {
    if (!metricsCategoryKeys[categoryKey]) {
      // remove flow metrics
      delete currentMetricCategory[categoryKey];
      return;
    }

    if (excludeCategories.includes(categoryKey)) {
      delete currentMetricCategory[categoryKey];
      return;
    }

    if (Array.isArray(excludeMetrics) && excludeMetrics.length) {
      excludeMetrics.forEach((excludeKey) => {
        if (currentMetricCategory[categoryKey][excludeKey]) {
          delete currentMetricCategory[categoryKey][excludeKey];
        }
      });
    }

    if (limitation && limitation?.metrics) {
      if (limitation.metrics.excludeCategories.includes(categoryKey)) {
        delete currentMetricCategory[categoryKey];
        return;
      }
      if (
        Array.isArray(limitation.metrics.excludeKeys) &&
        limitation.metrics.excludeKeys.length > 0
      ) {
        limitation.metrics.excludeKeys.forEach((excludeKey) => {
          if (currentMetricCategory[categoryKey][excludeKey]) {
            delete currentMetricCategory[categoryKey][excludeKey];
          }
        });
      }
      if (
        Array.isArray(limitation.metrics.excludeTypes) &&
        limitation.metrics.excludeTypes.length > 0
      ) {
        limitation.metrics.excludeTypes.forEach((excludeType) => {
          const categoryMetrics = currentMetricCategory[categoryKey];
          Object.keys(categoryMetrics).map((metricKey) => {
            if (categoryMetrics[metricKey] && categoryMetrics[metricKey].type === excludeType) {
              delete categoryMetrics[metricKey];
            }
          });
        });
      }
    }
  });

  return currentMetricCategory;
}

const DEFAULT_EXCLUDE_CATEGORIES = [];
const DEFAULT_EXCLUDE_METRICS = [];

function useSupportedMetrics({
  excludeCategories = DEFAULT_EXCLUDE_CATEGORIES,
  excludeMetrics = DEFAULT_EXCLUDE_METRICS,
  limitation,
  allMetrics,
}: any) {
  //TODO  use get state instance
  const { metricsCategoryKeys } = metricsStore.getState();
  const metricCategories = useMemo(() => {
    if (limitation?.context?.type === 'flow' && limitation?.context?.id) {
      const currentFlowMeta = metricsStore.getState()?.flowConfigMeta?.[limitation?.context?.id];
      const flowMetrics = generateFlowMetrics(allMetrics, currentFlowMeta);
      const updatedMetrics = _.omit(flowMetrics, excludeMetrics);

      return createMetricsGroupedByCategoryForAProduct({
        metrics: updatedMetrics,
        activeOnly: true,
        groupByFlow: false,
      });
    }

    if (limitation?.context?.type === 'frustration-signal' && limitation?.context?.id) {
      const currentFrustrationSignalMeta =
        metricsStore.getState()?.frustrationSignalConfigMeta?.[limitation?.context?.id];

      // Apply frustration signal display names to all metrics
      const frustrationSignalMetrics = generateFrustrationSignalMetrics(
        allMetrics,
        currentFrustrationSignalMeta,
      );

      // Merge the updated frustration signal metrics back into allMetrics
      const updatedAllMetrics = {
        ...allMetrics,
        ...frustrationSignalMetrics,
      };

      // Now apply normal limitation filtering (which will exclude other frustration signal metrics and unwanted categories)
      const metricsGroupedByCategory = createMetricsGroupedByCategoryForAProduct({
        metrics: updatedAllMetrics,
        activeOnly: true,
        groupByFlow: true,
      });

      const currentMetricCategory = applyLimitationFilters(
        metricsGroupedByCategory,
        metricsCategoryKeys,
        excludeCategories,
        excludeMetrics,
        limitation,
      );

      return currentMetricCategory;
    }

    const metricsGroupedByCategory = createMetricsGroupedByCategoryForAProduct({
      metrics: allMetrics,
      activeOnly: true,
      groupByFlow: true,
    });

    const currentMetricCategory = applyLimitationFilters(
      metricsGroupedByCategory,
      metricsCategoryKeys,
      excludeCategories,
      excludeMetrics,
      limitation,
    );

    return currentMetricCategory;
  }, [excludeCategories, excludeMetrics, allMetrics, limitation]);

  return metricCategories;
}

export default useSupportedMetrics;
